<template>
  <div class="chart-container">
    <Bar
      ref="chartRef"
      :data="chartData"
      :options="chartOptions"
      :redraw="true"
      style="max-height: 400px;"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import axios from '@/services/axios'
import { Bar } from 'vue-chartjs'
import {
  Chart,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

// 1️⃣ Registro dos componentes do Chart.js
Chart.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
)

// 2️⃣ Props para o período
const props = defineProps({
  dataInicio: { type: String, required: true },
  dataFim:    { type: String, required: true }
})

// 3️⃣ Estado reativo dos dados
const chartData = ref({
  labels: [],
  datasets: []
})

// 4️⃣ Opções de configuração (empilhado)
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: 'Prospecções por Mês',
      align: 'start'
    },
    legend: {
      display: false,
      position: 'right',
      align: 'center'
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      callbacks: {
        label(ctx) {
          const val = ctx.parsed.y
          return `${ctx.dataset.label}: ${val}`
        },
        footer(ctxs) {
          const total = ctxs
            .map(c => c.parsed.y)
            .reduce((s, v) => s + v, 0)
          return `Total no mês: ${total}`
        }
      }
    },
  },
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true,
      beginAtZero: true
    }
  }
}

// 5️⃣ Função de fetch e montagem dos datasets
async function fetchProspecoes() {
  try {
    const { data: resp } = await axios.get(
      '/gestao/graficos/individuais/classificacao-mensal',
      { params: { dataInicio: props.dataInicio, dataFim: props.dataFim } }
    )
    // resp.labels: ['01/2025', '02/2025', ...]
    // resp.series: { Quente: [10,5,...], Frio: [...], ... }

    // Monta os datasets para cada classificação
    const palette = [
      '#4A90E2', // Matrículas
      '#1ABC9C', // Mensalidades
      '#F5A623', // Aulas Avulsas
      '#E57373', // Prospecções (vermelho suave)
      '#9B59B6', // Vendas da Loja
      '#34495E'  // Receitas Gerais
    ]
    const classifications = Object.keys(resp.series)

    chartData.value = {
      labels: resp.labels,
      datasets: classifications.map((classif, idx) => ({
        label: classif,
        data: resp.series[classif],
        backgroundColor: palette[idx % palette.length]
      }))
    }
  } catch (e) {
    console.error('Erro ao carregar prospecções mensais:', e)
  }
}

// 6️⃣ Chama ao montar e ao mudar props
onMounted(fetchProspecoes)
watch(
  () => [props.dataInicio, props.dataFim],
  fetchProspecoes
)
</script>

<style scoped>
.chart-container {
  width: 100%;
  /* altura em porcentagem do pai ou fixa, ajuste conforme necessidade */
  height: 300px;
}
@media (min-width: 768px) {
  .chart-container {
    height: 400px;
  }
}
</style>
